import React, { useEffect, useImperativeHandle, useRef, useState } from 'react';

import { Button } from 'primereact/button';
import { Checkbox } from 'primereact/checkbox';
import { Dialog } from 'primereact/dialog';
import { Divider } from 'primereact/divider';
import { InputSwitch } from 'primereact/inputswitch';
import { MultiSelect } from 'primereact/multiselect';
import { Toast } from 'primereact/toast';
import { SubmitHandler } from 'react-hook-form';

import useVerificationFunctionsService from '../hooks/services/useVerificationFunctionsService';
import { FieldFilling, FieldFillingFunctionType } from '../types/interfaces';
import { ObjectForSelection } from '../types/interfaces/ObjectForSelection';
import { fieldFillingControlProps } from '../types/props';

import './css/FieldFillingControl.css';
import ConfirmationDialog from './shared/ConfirmationDialog';

interface RefInterface {
  handleCancel: () => void;
}

const FieldFillingControl = React.forwardRef<
  RefInterface | null,
  fieldFillingControlProps
>(({ fieldFillings, onUpdate, closeDialog }: fieldFillingControlProps, ref) => {
  const [localFieldFillings, setLocalFieldFillings] =
    useState<FieldFilling[]>(fieldFillings);
  const [showDialog, setShowDialog] = useState(false);
  const [unsavedChanges, setUnsavedChanges] = useState(false);
  const toast = useRef<Toast>(null);

  const [verificationFunctions, setVerificationFunctions] = useState<
    ObjectForSelection[]
  >([]);
  const verificationFunctionsService = useVerificationFunctionsService();
  useEffect(() => {
    const serviceResult = verificationFunctionsService.getForSelection();
    serviceResult.result.then((r) => {
      if (r) setVerificationFunctions(r);
    });
    return () => {
      if (serviceResult) serviceResult.cancel();
    };
  }, [verificationFunctionsService]);

  const handleCheckboxChange = (
    index: number,
    property: keyof FieldFilling
  ) => {
    setLocalFieldFillings((prevFieldFillings) => {
      const updatedFieldFillings = [...prevFieldFillings];
      updatedFieldFillings[index] = {
        ...updatedFieldFillings[index],
        [property]: !updatedFieldFillings[index][property]
      };
      setUnsavedChanges(true);
      return updatedFieldFillings;
    });
  };

  const handleMultiSelectChange = (
    index: number,
    selectedFunctions: FieldFillingFunctionType[]
  ) => {
    setLocalFieldFillings((prevFieldFillings) => {
      const updatedFieldFillings = [...prevFieldFillings];
      updatedFieldFillings[index] = {
        ...updatedFieldFillings[index],
        currentFunctions: selectedFunctions.map(
          (selected: FieldFillingFunctionType) => ({
            label: selected.label,
            id: selected.id
          })
        )
      };
      setUnsavedChanges(true);
      return updatedFieldFillings;
    });
  };

  const onSubmit: SubmitHandler<FieldFilling[]> = (data) => {
    if (onUpdate) {
      onUpdate(data);
    }
    closeDialog && closeDialog();
  };

  const handleCancel = () => {
    if (unsavedChanges) {
      setShowDialog(true);
    }
    if (closeDialog && !unsavedChanges) {
      closeDialog();
    }
  };

  useImperativeHandle(ref, () => {
    return {
      handleCancel
    };
  });

  const handleConfirmationContinue = () => {
    setShowDialog(false);
    if (closeDialog) {
      closeDialog();
    }
  };

  const handleConfirmationCancel = () => {
    setShowDialog(false);
  };

  const isSimpleFieldRow = (fieldType: number) =>
    fieldType === 11 || fieldType === 12 || fieldType === 13;

  return verificationFunctions.length > 0 ? (
    <div className="dialog-tabless-card">
      <Toast ref={toast} />
      <div className="flex flex-column pb-0">
        <div className="dialog-tabless-card__header-gap flex flex-column">
          <Divider />
          <div className="field-filling-constructor-card">
            <div className="flex flex-column">
              <div
                className="scrollable-container"
                id="tags-scroll"
                style={{
                  width: '100%',
                  height: 'calc(89vh - 224px)',
                  overflow: 'auto'
                }}
              >
                <div
                  className="flex flex-column scrollable-content"
                  style={{ width: '99%' }}
                >
                  {localFieldFillings.map((item: FieldFilling, index: number) => (
                    <div
                      key={item.id}
                      className="field-filling-constructor-card__row"
                    >
                      <div
                        className={!isSimpleFieldRow(item.fieldType) ? 'flex flex-row justify-content-between align-items-center gap-3 pl-3' : 'flex flex-row  align-items-center gap-5 pl-3'}
                        style={{
                          marginBottom:
                            index !== localFieldFillings.length - 1 ? '16px' : ''
                        }}
                      >
                        
                        <div className="flex align-items-center justify-content-between gap-3">
                          <InputSwitch 
                            checked={item.isActive}
                            onChange={() => handleCheckboxChange(index, 'isActive')}
                          />
                          <label style={{width: 135}}>{item.name}</label>
                        </div>
                        
                        <div className="flex align-items-center">
                          <Checkbox
                            checked={item.isRequired || false}
                            onChange={() =>
                              handleCheckboxChange(index, 'isRequired')
                            }
                          />
                          <label
                            style={{ marginLeft: '14px' }}
                            htmlFor={`fieldFillings[${index}].required` as const}
                          >
                            Обязательный
                          </label>
                        </div>
                        {!isSimpleFieldRow(item.fieldType) && (
                          <>
                            <div className="flex align-items-center">
                              <Checkbox
                                checked={item.isResettable}
                                onChange={() =>
                                  handleCheckboxChange(index, 'isResettable')
                                }
                              />
                              <label
                                style={{ marginLeft: '14px' }}
                                htmlFor={
                                  `fieldFillings[${index}].isResettable` as const
                                }
                              >
                                Сбрасывать
                              </label>
                            </div>
                            <MultiSelect
                              value={item.currentFunctions}
                              options={verificationFunctions}
                              onChange={(e) => {
                                const selectedValues =
                                  e.value as FieldFillingFunctionType[];
                                handleMultiSelectChange(index, selectedValues);
                              }}
                              placeholder="Выберите функции"
                              className="w-full"
                              filter
                            />
                          </>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>

            </div>
          </div>
        </div>
      </div>

      <div className="default-card__buttons">
        <div className="flex gap-3">
          <Button
            label="Принять"
            icon="pi pi-check"
            onClick={() => onSubmit(localFieldFillings)}
          />
          <Button
            label="Отмена"
            icon="pi pi-times"
            onClick={handleCancel}
            className="p-button-secondary"
          />
        </div>
      </div>

      <ConfirmationDialog
        visible={showDialog}
        onCancel={handleConfirmationCancel}
        onContinue={handleConfirmationContinue}
      />
    </div>
  ) : (
    <React.Fragment />
  );
});

FieldFillingControl.displayName = 'FieldFillingControl';

export default FieldFillingControl;
